// 8F этап 2/3 — результаты попунктной проверки ТЗ/ТУ ↔ ПД (судья покрытия).
// Судья предлагает — человек подтверждает («В замечания» вручную, ПРД §13.1).
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, RefreshCw, Send, ChevronDown, ChevronRight, FileSearch } from 'lucide-react';
import { api } from '@/api/client';
import { DocTraceSlideOver } from '@/components/DocTracePreview';
import { PayGateBanner } from '@/components/PayGateBanner';
import { useMarkup } from '@/hooks/useMarkup';
import { MarkupToggle, MarkupFields } from '@/components/MarkupUI';
import type { Project } from '@/types/api';
import { canRunCheck, runBlockReason, tariffErrorMessage, tariffStateFromProject } from '@/lib/tariff';

interface Finding {
  id: string;
  verdict: 'fulfilled' | 'partial' | 'mismatch' | 'not_covered' | 'manual';
  reasoning?: string | null;
  citation?: string | null;
  actual_value?: string | null;
  promoted_remark_id?: string | null;
  source: 'ТЗ' | 'ТУ';
  item_no: string | null;
  text: string;
  req_type: string;
  page?: number | null;
  // 15.A3: якорь трассируемости на лист ПД, откуда взята цитата-ответ.
  evidence_document_id?: string | null;
  evidence_page?: number | null;
  evidence_verbatim?: string | null;
}

interface Result {
  has_result: boolean;
  status?: string;
  progress?: { done: number; total: number };
  counts?: Record<string, number>;
  conclusion?: { level: 'compliant' | 'not_confirmed' | 'non_compliant'; title: string; text: string } | null;
  error?: string | null;
  finished_at?: string | null;
  findings: Finding[];
}

const CONCLUSION_META = {
  compliant:     { color: '#047857', bg: 'rgba(16,185,129,.07)',  border: 'rgba(16,185,129,.3)' },
  not_confirmed: { color: '#B45309', bg: 'rgba(245,158,11,.08)',  border: 'rgba(245,158,11,.35)' },
  non_compliant: { color: '#B91C1C', bg: 'rgba(239,68,68,.07)',   border: 'rgba(239,68,68,.35)' },
} as const;

const V_META: Record<Finding['verdict'], { label: string; color: string; bg: string }> = {
  mismatch:    { label: 'Отступление',   color: '#B91C1C', bg: 'rgba(239,68,68,.10)' },
  not_covered: { label: 'Не отражено',   color: '#B45309', bg: 'rgba(245,158,11,.12)' },
  partial:     { label: 'Частично',      color: '#C2410C', bg: 'rgba(249,115,22,.10)' },
  manual:      { label: 'Вручную',       color: '#6B7280', bg: 'rgba(107,114,128,.10)' },
  fulfilled:   { label: 'Выполнено',     color: '#047857', bg: 'rgba(16,185,129,.10)' },
};

type Filter = 'problems' | 'all' | 'fulfilled' | 'manual';
const PROBLEMS: Finding['verdict'][] = ['mismatch', 'not_covered', 'partial'];

interface ProceduralItem {
  id: string;
  source: 'ТЗ' | 'ТУ';
  item_no: string | null;
  text: string;
  req_type: string;
  excluded: boolean;
}

export function TaskComplianceResults({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('problems');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [traceFinding, setTraceFinding] = useState<Finding | null>(null); // 15.A3 slide-over
  // Пирамида (решение владельца 2026-07-03): список пунктов свёрнут,
  // разворачивается справочно. Наверху — только заключение и счётчики.
  const [showList, setShowList] = useState(false);
  const [showProcedural, setShowProcedural] = useState(false);
  const [tariffErr, setTariffErr] = useState<string | null>(null);
  const mk = useMarkup(projectId, 'task');  // 15.G разметка заказчика
  useEffect(() => { if (mk.markupMode) setShowList(true); }, [mk.markupMode]);

  // 15.E Reading B: тариф проекта — per-check гейт кнопки запуска.
  const projectQ = useQuery<Project>({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    staleTime: 1000 * 30,
  });
  const tariffState = tariffStateFromProject(projectQ.data);
  const runAllowed = canRunCheck(tariffState, 'task');
  const runGateText = runBlockReason(tariffState, 'task');

  const { data } = useQuery<Result>({
    queryKey: ['task-compliance', projectId],
    queryFn: () => api.get<Result>(`/projects/${projectId}/task-compliance`),
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 3000 : false),
  });
  // Процедурные пункты (согласования, сроки, экземпляры) не судятся против ПД —
  // показываем отдельной свёрнутой группой. Кэш общий с реестром внизу вкладки.
  const { data: reg } = useQuery<{ items: ProceduralItem[] }>({
    queryKey: ['task-requirements', projectId],
    queryFn: () => api.get<{ items: ProceduralItem[] }>(`/projects/${projectId}/task-requirements`),
  });
  const procedural = (reg?.items ?? []).filter((i) => i.req_type === 'procedural' && !i.excluded);

  const run = async () => {
    try {
      setTariffErr(null);
      await api.post(`/projects/${projectId}/task-compliance/run`, {});
      qc.invalidateQueries({ queryKey: ['task-compliance', projectId] });
    } catch (e) {
      // 15.E: тарифный гейт (402) — upsell, не сырая ошибка.
      const msg = tariffErrorMessage(e);
      setTariffErr(msg ?? (e instanceof Error ? e.message : 'Не удалось запустить проверку'));
    }
  };

  const promote = async (f: Finding) => {
    setBusy(f.id);
    try {
      await api.post(`/projects/${projectId}/task-compliance/finding/${f.id}/promote`, {});
      qc.invalidateQueries({ queryKey: ['task-compliance', projectId] });
      qc.invalidateQueries({ queryKey: ['readiness-summary', projectId] });
    } finally {
      setBusy(null);
    }
  };

  const findings = data?.findings ?? [];
  const running = data?.status === 'running';
  const problems = findings.filter((f) => PROBLEMS.includes(f.verdict)).length;
  const visible = findings.filter((f) => {
    if (filter === 'all') return true;
    if (filter === 'problems') return PROBLEMS.includes(f.verdict);
    return f.verdict === filter;
  });

  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 24, marginBottom: 20 }}>
      <div className="flex items-start" style={{ gap: 12, marginBottom: 12 }}>
        <ShieldCheck size={20} stroke="#0A1628" style={{ marginTop: 2 }} />
        <div style={{ flex: 1 }}>
          <div className="text-ink flex items-center" style={{ fontSize: 14, fontWeight: 600, gap: 8 }}>
            Проверка соответствия — каждый пункт задания против документации
            <span className="rounded-pill" style={{ padding: '1px 8px', fontSize: 10, fontWeight: 700, background: 'rgba(245,158,11,.15)', color: '#B45309' }}>
              бета — идёт калибровка
            </span>
          </div>
          <div className="text-muted" style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 680 }}>
            По каждому невисключённому пункту реестра судья ищет подтверждение в ПД.
            Числовые требования сверяются детерминированно (не менее/не более).
            Вердикты — предложение: решение о замечании за вами.
          </div>
        </div>
        <button type="button" onClick={run} disabled={running || !runAllowed}
          style={{ padding: '8px 16px', borderRadius: 20, fontSize: 12.5, fontWeight: 600,
                   background: (running || !runAllowed) ? '#E5E7EB' : '#0A1628',
                   color: (running || !runAllowed) ? '#6B7280' : '#FFF',
                   border: 'none', cursor: (running || !runAllowed) ? 'default' : 'pointer',
                   display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={13} className={running ? 'animate-spin' : undefined} />
          {running ? 'Проверяю…' : 'Проверить соответствие'}
        </button>
      </div>

      {!runAllowed && runGateText && <PayGateBanner projectId={projectId} reason={runGateText} />}
      {tariffErr && (
        <p style={{ color: '#B45309', fontSize: 12.5, margin: '0 0 10px', maxWidth: 680 }}>{tariffErr}</p>
      )}

      {running && data?.progress && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ height: 7, borderRadius: 5, background: '#F3F4F6', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(100 * data.progress.done) / Math.max(1, data.progress.total)}%`, background: '#0A1628', transition: 'width .5s' }} />
          </div>
          <div className="text-muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            пунктов проверено: {data.progress.done} из {data.progress.total}
          </div>
        </div>
      )}
      {data?.status === 'error' && (
        <div style={{ padding: 10, borderRadius: 8, background: 'rgba(239,68,68,.08)', color: '#B91C1C', fontSize: 12.5, marginBottom: 10 }}>
          Прогон упал: {data.error || 'причина не указана'}
        </div>
      )}

      {!data?.has_result && !running && (
        <div className="text-muted text-center" style={{ padding: '24px 16px', border: '1px dashed #E5E7EB', borderRadius: 10, fontSize: 12.5 }}>
          Проверка ещё не запускалась. Сначала провалидируйте реестр пунктов выше,
          затем нажмите «Проверить соответствие».
        </div>
      )}

      {data?.has_result && (
        <>
          {data.conclusion && (() => {
            const cm = CONCLUSION_META[data.conclusion.level] ?? CONCLUSION_META.not_confirmed;
            return (
              <div style={{ padding: '14px 18px', borderRadius: 10, background: cm.bg,
                            border: `1px solid ${cm.border}`, marginBottom: 14 }}>
                <div className="font-heading" style={{ fontSize: 15.5, fontWeight: 650, color: cm.color }}>
                  {data.conclusion.title}
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.55, color: '#374151', marginTop: 4 }}>
                  {data.conclusion.text}
                </div>
              </div>
            );
          })()}
          <div style={{ margin: '4px 0 10px' }}>
            <MarkupToggle on={mk.markupMode} onToggle={() => mk.setMarkupMode((v) => !v)} savedAt={mk.savedAt} />
          </div>
          <button type="button" onClick={() => setShowList(!showList)}
            className="flex items-center"
            style={{ gap: 6, padding: '8px 0', background: 'none', border: 'none',
                     cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#374151' }}>
            {showList ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            {showList ? 'Свернуть список пунктов' : `Показать пункты проверки · ${findings.length}`}
            <span className="text-muted" style={{ fontWeight: 400, marginLeft: 6 }}>
              {problems ? `из них проблемных: ${problems}` : 'проблемных нет'}
            </span>
            {data.finished_at && (
              <span className="text-muted" style={{ fontSize: 11, fontWeight: 400, marginLeft: 'auto' }}>
                проверено {new Date(data.finished_at).toLocaleString('ru-RU')}
              </span>
            )}
          </button>

          {showList && (
          <>
          <div className="flex items-center" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {([['problems', `Проблемные · ${problems}`],
               ['all', `Все · ${findings.length}`],
               ['fulfilled', `Выполнено · ${findings.filter((f) => f.verdict === 'fulfilled').length}`],
               ['manual', `Вручную · ${findings.filter((f) => f.verdict === 'manual').length}`],
              ] as [Filter, string][]).map(([id, label]) => (
              <button key={id} type="button" onClick={() => setFilter(id)}
                style={{ padding: '4px 11px', borderRadius: 15, fontSize: 11.5,
                         fontWeight: filter === id ? 600 : 400,
                         border: filter === id ? '1px solid #0A1628' : '1px solid #E5E7EB',
                         background: filter === id ? '#0A1628' : 'transparent',
                         color: filter === id ? '#FFF' : '#6B7280', cursor: 'pointer' }}>
                {label}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {visible.map((f) => {
              const m = V_META[f.verdict];
              const isOpen = expanded === f.id;
              const isProblem = PROBLEMS.includes(f.verdict);
              return (
                <div key={f.id} style={{ border: '1px solid #F3F4F6', borderRadius: 8, overflow: 'hidden' }}>
                  <button type="button" onClick={() => setExpanded(isOpen ? null : f.id)}
                    style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10,
                             padding: '9px 12px', background: '#FFF', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                    <span className="rounded-pill" style={{ padding: '2px 9px', fontSize: 10.5, fontWeight: 700,
                                                            background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>
                      {m.label}
                    </span>
                    <span className="tabular text-muted" style={{ fontSize: 11.5, fontWeight: 600, minWidth: 56 }}>
                      {f.source} {f.item_no || '—'}
                    </span>
                    <span style={{ flex: 1, fontSize: 12.5, lineHeight: 1.45, color: '#1F2937' }}>
                      {f.text.slice(0, 160)}{f.text.length > 160 ? '…' : ''}
                    </span>
                    {f.promoted_remark_id && (
                      <span style={{ fontSize: 10.5, color: '#047857', whiteSpace: 'nowrap' }}>✓ в замечаниях</span>
                    )}
                  </button>
                  {mk.markupMode && (
                    <div style={{ padding: '0 12px 10px' }}>
                      <MarkupFields value={mk.labels[f.id]} onChange={(p) => mk.setLabel(f.id, p)} />
                    </div>
                  )}
                  {isOpen && (
                    <div style={{ padding: '10px 14px', borderTop: '1px solid #F3F4F6', background: '#FAFAFA', fontSize: 12.5 }}>
                      {f.reasoning && <p style={{ lineHeight: 1.5, color: '#374151', marginBottom: 8 }}>{f.reasoning}</p>}
                      {f.citation && (
                        <div style={{ padding: '7px 11px', borderLeft: '3px solid #E5E7EB', background: '#FFF', borderRadius: 4, fontSize: 12, color: '#374151', marginBottom: 8, lineHeight: 1.5 }}>
                          …{f.citation}…
                        </div>
                      )}
                      {f.actual_value && (
                        <div className="text-muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
                          фактическое значение в ПД: <b>{f.actual_value}</b>
                        </div>
                      )}
                      <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
                        {isProblem && !f.promoted_remark_id && (
                          <button type="button" onClick={() => promote(f)} disabled={busy === f.id}
                            style={{ padding: '6px 14px', borderRadius: 16, fontSize: 11.5, fontWeight: 600,
                                     background: '#0A1628', color: '#FFF', border: 'none', cursor: 'pointer',
                                     display: 'flex', alignItems: 'center', gap: 5, opacity: busy === f.id ? 0.6 : 1 }}>
                            <Send size={11} /> {busy === f.id ? 'Создаю…' : 'В замечания'}
                          </button>
                        )}
                        {f.evidence_document_id && (f.evidence_verbatim || f.citation) && (
                          <button type="button" onClick={() => setTraceFinding(f)}
                            title="Открыть лист документа с подсветкой цитаты-ответа"
                            style={{ padding: '6px 14px', borderRadius: 16, fontSize: 11.5,
                                     background: 'transparent', color: '#6B7280', border: '1px solid #E5E7EB',
                                     cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                            <FileSearch size={11} /> Показать в документе
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
          )}

          {procedural.length > 0 && (
            <div style={{ marginTop: 10, borderTop: '1px solid #F3F4F6', paddingTop: 10 }}>
              <button type="button" onClick={() => setShowProcedural(!showProcedural)}
                className="flex items-center"
                style={{ gap: 6, background: 'none', border: 'none', cursor: 'pointer',
                         fontSize: 12, color: '#6B7280', padding: 0 }}>
                {showProcedural ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Процедурные пункты задания · {procedural.length} — проверяются вне документации
              </button>
              {showProcedural && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div className="text-muted" style={{ fontSize: 11.5, lineHeight: 1.5, maxWidth: 680 }}>
                    Согласования, сроки, экземпляры, порядок передачи и оплаты — эти условия
                    выполняются в процессе работ, по содержанию проектной документации они
                    не проверяются и в статистику отступлений не входят.
                  </div>
                  {procedural.map((p) => (
                    <div key={p.id} className="flex items-start"
                         style={{ gap: 10, padding: '7px 11px', border: '1px solid #F3F4F6',
                                  borderRadius: 8, background: '#FAFAFA' }}>
                      <span className="tabular text-muted" style={{ fontSize: 11.5, fontWeight: 600, minWidth: 56 }}>
                        {p.source} {p.item_no || '—'}
                      </span>
                      <span className="text-muted" style={{ flex: 1, fontSize: 12, lineHeight: 1.45 }}>
                        {p.text}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {traceFinding && (
        <DocTraceSlideOver
          title={`${traceFinding.source} ${traceFinding.item_no || ''}`.trim()}
          subtitle={traceFinding.text}
          quote={traceFinding.citation || null}
          searchQuote={traceFinding.evidence_verbatim || traceFinding.citation || null}
          documentId={traceFinding.evidence_document_id}
          page={traceFinding.evidence_page ?? null}
          onClose={() => setTraceFinding(null)}
        />
      )}
    </div>
  );
}
